import { useCallback, useState } from 'react';

import { useUploadFiles } from 'common/hooks/useUploadFiles';
import { ICSVData } from 'common/types';
import { decodeTextFromBuffer } from 'common/utils/encoding';
import { IImportData } from 'user/types';
import { parseUserCsv } from 'user/utils/csv';

interface IUseUserImportOptions {
    onImportComplete?: () => void;
    onUploadError?: (fileName: string, message: string) => void;
}

export const useUserImport = (options?: IUseUserImportOptions) => {
    const { onImportComplete, onUploadError } = options || {};

    const [importData, setImportData] = useState<{
        csvData?: ICSVData;
        fileId?: string;
    } | null>(null);

    const completeImportData: IImportData | null =
        importData?.csvData && importData?.fileId
            ? { csvData: importData.csvData, fileId: importData.fileId }
            : null;

    const importModalOpen = !!completeImportData;

    const { uploadFiles } = useUploadFiles({
        onSuccess: (_fileIndex, uploadedFile) => {
            setImportData((prev) =>
                prev
                    ? { ...prev, fileId: uploadedFile.id }
                    : { fileId: uploadedFile.id }
            );
        },
        onError: (fileName, message) => {
            setImportData(null);
            onUploadError?.(fileName, message);
        },
    });

    const handleFileUpload = useCallback(
        (files: File[]) => {
            const file = files[0];

            if (!file) return;

            setImportData(null);

            const reader = new FileReader();

            reader.onload = (e) => {
                const buffer = e.target?.result;

                if (!(buffer instanceof ArrayBuffer)) return;

                const content = decodeTextFromBuffer(buffer);
                const csvData = parseUserCsv(content);

                setImportData((prev) =>
                    prev ? { ...prev, csvData } : { csvData }
                );
            };

            reader.readAsArrayBuffer(file);

            // Upload the file to get a fileId for the import mutation
            uploadFiles([file], 10);
        },
        [uploadFiles]
    );

    const handleImportModalClose = useCallback(() => {
        setImportData(null);
        onImportComplete?.();
    }, [onImportComplete]);

    return {
        completeImportData,
        importModalOpen,
        handleFileUpload,
        handleImportModalClose,
    };
};
